/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useCallback, useEffect } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { useViewport } from 'src/contexts/viewport';
import { useStores } from 'src/contexts/stores';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDutchiePay } from 'src/payments/hooks';

/**
 * This is the least bad solution that we came up with to solve positioning issues created by trying to render a fullscreen
 * iframe within an embedded menu iframe. The hook observes the appearance of the Plaid link modal, which is an iframe,
 * and updates its dimensions when the viewport changes. For embedded menus this is necessary because the Plaid iframe
 * cannot set its own dimensions correctly when opened within our iframe.
 *
 * On mobile devices, we also need to override some styles that Plaid adds when they dynamically load a mobile-only stylesheet.
 * We do this by creating our own stylesheet and appending it to the end of the head tag. This stylesheet
 * is then used to override the Plaid styles with `!important` to ensure that our styles take precedence.
 * The crazy idOverride selector is necessary because Plaid uses that ID string to create a
 * nuclear level of specificity in their mobile stylesheet. It is a hack, but it works.
 *
 * The observer is only active on embedded menus with Pay by Bank enabled.
 *
 */

// Test to make sure that the node passed in is both the Plaid iframe and is visible
const shouldReposition = (node: HTMLElement): boolean =>
  node instanceof HTMLIFrameElement &&
  Boolean(node.getAttribute('id')?.includes('plaid-link-iframe')) &&
  node.style.display !== 'none';

const usePlaidIframeObserver = (): void => {
  const viewport = useViewport();
  const { UI, Cart } = useStores();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down(`xs`));

  const flags = useFlags();
  const plaidEmbeddedSearchEnabled = flags['fintech.platform.dutchiepay.plaid-embedded-search'] ?? false;
  const { isDutchiePayEnabledForDispo } = useDutchiePay();
  const shouldObservePlaidIframe = UI.isEmbedded && plaidEmbeddedSearchEnabled && isDutchiePayEnabledForDispo;
  const { embeddedEventDispatcher } = UI;

  const enableFullScreenEmbeddedCheckout =
    Cart.order?.dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout ?? false;

  const positionPlaidIframe = useCallback(
    (plaidIframe) => {
      const topDesktop = enableFullScreenEmbeddedCheckout ? 0 : viewport.parentOffset;
      const mobileHeight = enableFullScreenEmbeddedCheckout ? viewport.height : viewport.height - viewport.offsetTop;

      if (plaidIframe.style.display === 'none') {
        return;
      }

      if (isMobile) {
        embeddedEventDispatcher('iframe:scrollToTop', { force: true });
        // Match the ID that Plaid adds for mobile overrides.
        const idOverride = `html#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id#plaid-link-temporary-id > body > .plaid-link-iframe`;

        // Construct the CSS rule string with !important
        const cssRule = `${idOverride} { 
          height: ${mobileHeight}px !important; 
          min-height: ${mobileHeight}px !important;
          width: 100% !important;
          top: 0px !important;
        }`;

        // Create a new stylesheet with the mobile override rule, and append it before the closing head tag
        const sheet = (function appendStylesheet() {
          const style = document.createElement('style');
          style.setAttribute('id', 'plaid-mobile-override');
          document.head.appendChild(style);
          return style.sheet;
        })();

        // Insert the rule into the stylesheet
        sheet?.insertRule(cssRule, sheet.cssRules.length);
      } else {
        plaidIframe.style.height = `${viewport.height}px`;
        plaidIframe.style.width = '100%';
        plaidIframe.style.top = `${topDesktop}px`;
      }
    },
    [
      embeddedEventDispatcher,
      enableFullScreenEmbeddedCheckout,
      isMobile,
      viewport.height,
      viewport.offsetTop,
      viewport.parentOffset,
    ]
  );

  useEffect(() => {
    if (!shouldObservePlaidIframe) {
      return undefined;
    }

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'childList') {
          Array.from(mutation.addedNodes).forEach((node) => {
            if (shouldReposition(node as HTMLElement)) {
              positionPlaidIframe(node);
            }
          });
        } else if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const node = mutation.target;
          if (shouldReposition(node as HTMLElement)) {
            positionPlaidIframe(node);
          }
        }
      });
    });

    // Start observing the body for changes
    observer.observe(document.body, {
      attributes: true, // Observe attribute changes
      attributeFilter: ['style'], // Only observe changes to the 'style' attribute
      childList: true, // Observe addition/removal of child nodes
      subtree: true, // Observe all descendants
    });

    return () => {
      observer.disconnect();
    };
  }, [positionPlaidIframe, shouldObservePlaidIframe]);
};

export default usePlaidIframeObserver;
